/**
 * Text content and configuration for Research Insights.
 * Separated from component code for maintainability.
 */

// ── Insight colors ───────────────────────────────────────────────

export const INSIGHT_COLORS = {
  scoreHigh: "#3BA974",   // green — composite score ≥ 0.7
  scoreMid: "#F8BC3B",    // amber — composite score ≥ 0.5
  scoreLow: "#E92519",    // red   — composite score < 0.5

  // Pill colors — reused for dimension tags (HIGH/MODERATE/LOW) and sentiment pills (POSITIVE/NEUTRAL/NEGATIVE)
  pillHigh: "#98E53A",    // bright green — HIGH / POSITIVE
  pillMid: "#FAD417",     // yellow       — MODERATE / NEUTRAL
  pillLow: "#FF5454",     // bright red   — LOW / NEGATIVE
};

export const getScoreColor = (score) => {
  if (score >= 0.7) return INSIGHT_COLORS.scoreHigh;
  if (score >= 0.5) return INSIGHT_COLORS.scoreMid;
  return INSIGHT_COLORS.scoreLow;
};

export const getPillConfig = (score10) => {
  if (score10 >= 8) return { label: "HIGH", color: INSIGHT_COLORS.pillHigh };
  if (score10 >= 5) return { label: "MODERATE", color: INSIGHT_COLORS.pillMid };
  return { label: "LOW", color: INSIGHT_COLORS.pillLow };
};

export const getSentimentConfig = (dominant) => {
  if (dominant === "positive") return { label: "Praise", color: INSIGHT_COLORS.pillHigh };
  if (dominant === "negative") return { label: "Critical", color: INSIGHT_COLORS.pillLow };
  return { label: "Mixed", color: INSIGHT_COLORS.pillMid };
};

// ── Scoring dimensions ───────────────────────────────────────────

export const DIMENSIONS = [
  {
    id: "volume",
    label: "Evidence Volume",
    weight: 0.25,
    formula: "documents with this pattern / total documents",
    explanation: "What fraction of your documents mention this insight. Each document counts once regardless of how many chunks it has.",
    formatDetail: (d) => `${d.documents} of ${d.totalDocuments} documents (${d.chunks} chunks)`,
  },
  // {
  //   id: "coverage",
  //   label: "User Coverage",
  //   weight: 0.20,
  //   formula: "unique users in insight / total unique users",
  //   explanation: "Falls back to document count if no user data. Tells you if this comes from many voices or one person repeating.",
  //   formatDetail: (d) =>
  //     d.users > 0
  //       ? `${d.users} of ${d.totalUsers} users (${d.coveragePct}%)`
  //       : `${d.documents} documents (${d.coveragePct}% coverage)`,
  // },
  // {
  //   id: "consistency",
  //   label: "Source Consistency",
  //   weight: 0.15,
  //   formula: "matched source types / total source types",
  //   explanation: "If only one content type exists, scores 100% by default (nothing to contradict).",
  //   formatDetail: (d) =>
  //     d.totalSources <= 1
  //       ? "Single source type"
  //       : `${d.matchedSources} of ${d.totalSources} source types (${d.consistencyPct}%)`,
  // },
  {
    id: "sentimentStrength",
    label: "Sentiment Strength",
    weight: 0.25,
    formula: "| mean(document polarity) |  where positive = +1, neutral = 0, negative = \u22121",
    explanation: "Each document gets one vote (dominant polarity of its chunks). Strong agreement (80% negative) \u2192 0.8. Mixed 50/50 \u2192 ~0.",
    formatDetail: (d) =>
      d.total === 0
        ? "No sentiment data"
        : `${d.dominant} (${d.dominantPct}%) \u2014 ${d.positive} pos, ${d.neutral} neutral, ${d.negative} neg docs`,
  },
  {
    id: "recency",
    label: "Recency",
    weight: 0.25,
    formula: "last 30 days \u2192 1.0, then 2^(\u2212days_after_30 / 90)",
    explanation: "Full score for the last month. After 30 days, decay starts: 3 months \u2192 0.5, 6 months \u2192 0.25. Scored per document.",
    formatDetail: (d) =>
      d.total === 0
        ? "No timestamp data"
        : `${d.recentCount} of ${d.total} docs from last ${d.windowDays} days (${d.recentPct}%)`,
  },
  {
    id: "contradiction",
    label: "Agreement",
    weight: 0.25,
    formula: "1 \u2212 (opposing docs / total docs with sentiment)",
    explanation: "If 2 of 20 docs disagree \u2192 0.9 (high agreement). Each document's dominant sentiment counts as one vote.",
    formatDetail: (d) =>
      d.total === 0
        ? "No sentiment data"
        : `${d.opposingPct}% opposing (${d.opposing} of ${d.total} docs)`,
  },
];

// ── V2 Findings scoring dimensions ──────────────────────────────

export const DIMENSIONS_V2 = [
  {
    id: "sourceDiversity",
    label: "Source Diversity",
    weight: 0.20,
    formula: "documents with this finding / total documents",
    explanation: "How many unique documents contribute evidence to this finding.",
    formatDetail: (d) =>
      d.documents != null
        ? `${d.documents} of ${d.total} documents (${d.pct || 0}%)`
        : "No data",
  },
  {
    id: "segmentSize",
    label: "Segment Size",
    weight: 0.15,
    formula: "average annotations per slice",
    explanation: "Larger slices mean the finding is based on more data points.",
    formatDetail: (d) =>
      d.noData
        ? "No data"
        : `${d.sliceCount || 0} slices, avg ${d.avgSliceSize || 0} annotations`,
  },
  {
    id: "distributionContrast",
    label: "Distribution Contrast",
    weight: 0.20,
    formula: "(max difference between slices - 20) / 80",
    explanation: "How different the segments are from each other. Only applies to segment contrast patterns.",
    formatDetail: (d) =>
      d.notApplicable
        ? `N/A: ${d.reason}`
        : `${d.diffPct || 0}pp difference (${d.sliceA} vs ${d.sliceB})`,
  },
  {
    id: "certaintyConsensus",
    label: "Certainty Consensus",
    weight: 0.15,
    formula: "weighted average of certainty levels",
    explanation: "How consistently annotations express certainty. Confirmed > Uncertain > Speculative.",
    formatDetail: (d) =>
      d.noData
        ? "No certainty data"
        : `${d.Confirmed || 0} confirmed, ${d.Uncertain || 0} uncertain, ${d.Speculative || 0} speculative`,
  },
  {
    id: "attributeAgreement",
    label: "Attribute Agreement",
    weight: 0.15,
    formula: "mean dominant-value fraction across sentiment, intent, discourse_role",
    explanation: "How well attributes agree across the evidence. Higher = clearer signal.",
    formatDetail: (d) =>
      d.noData
        ? "No attribute data"
        : `${d.dimensionsChecked || 0} dimensions checked`,
  },
  {
    id: "quantConfirmation",
    label: "Quant Confirmation",
    weight: 0.15,
    formula: "aligned quant signals / total quant signals",
    explanation: "Whether numeric field values (ratings, scores) confirm the qualitative signal.",
    formatDetail: (d) =>
      d.notApplicable
        ? `N/A: ${d.reason}`
        : `${d.alignment || 0}% alignment (${d.confirmed || 0} of ${d.total || 0})`,
  },
];

// ── Evidence Synthesis scoring dimensions ───────────────────────

export const DIMENSIONS_EVIDENCE_SYNTHESIS = [
  {
    id: "sourceDiversity",
    label: "Source Diversity",
    weight: 0.40,
    formula: "documents contributing / total documents",
    explanation: "How many unique documents provide evidence for this finding. Higher diversity = more representative.",
    formatDetail: (d) =>
      d.documents != null
        ? `${d.documents} of ${d.total} documents`
        : "No data",
  },
  {
    id: "evidenceStrength",
    label: "Evidence Strength",
    weight: 0.40,
    formula: "LLM-assessed strength: strong / moderate / emerging",
    explanation: "How strong the evidence is based on recurring patterns across quotes. Strong = clear pattern across multiple users. Emerging = signal from few users.",
    formatDetail: (d) =>
      d.label || "Not assessed",
  },
  {
    id: "quoteDensity",
    label: "Quote Density",
    weight: 0.20,
    formula: "supporting quotes / 4 (capped at 1.0)",
    explanation: "How many direct quotes support this finding. More quotes = more grounded finding.",
    formatDetail: (d) =>
      `${d.count || 0} supporting quotes`,
  },
];

// ── Annotation attribute labels ─────────────────────────────────

export const ATTRIBUTE_LABELS = {
  intent: "Intent",
  sentiment_polarity: "Sentiment",
  certainty: "Certainty",
  discourse_role: "Role",
  speech_act: "Speech Act",
  emotion_affect: "Emotion",
  expression_mode: "Expression",
  epistemic_status: "Epistemic",
  temporal_anchor: "Temporal",
  named_entities: "Entity",
  named_entity_category: "Entity Type",
};

// ── Insight type explanations ────────────────────────────────────

export const INSIGHT_TYPE_EXPLANATIONS = {
  prevalence:
    "This prevalence-based insight identifies a frequently occurring theme across your data. The tag appears in a significant portion of documents, indicating a dominant pattern.",
  cooccurrence:
    "This co-occurrence insight identifies tags that consistently appear together across multiple documents, suggesting a thematic relationship.",
  lift:
    "This association-based insight identifies tags that appear together significantly more often than random chance would predict.",
  conditional:
    "This conditional insight reveals that when one tag is present, another tag has a high probability of also appearing, suggesting a dependency.",
  similarity:
    "This similarity insight identifies tags that appear in overlapping sets of documents, behaving similarly even if they don't always co-occur.",
};

// ── Insight type labels (for tags in the list) ───────────────────

export const INSIGHT_TYPE_LABELS = {
  prevalence: "Prevalence",
  cooccurrence: "Co-occurrence",
  lift: "Association",
  conditional: "Conditional",
  similarity: "Similarity",
};
